// client/src/services/lexistarReplay.ts
//
// LexiStar replay API client (ST-00188, M3-A3; contracts docs/lexistar/contracts/
// m3-contracts.md §7.3; LS-03 FR-RPL-001/002/004/005/013; LS-09 §5.6). Server:
// server/modules/lexistar/replay, mounted at /api/lexistar.
//
// Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
// header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the
// response BODY:
//
//   { scope: {kind: 'trip'|'asset', asset_key, asset_keys, trip_id, from, to, tolerance_m},
//     asset: {id, registration, fleet_no, …} | null,
//     track: [{t, lat, lon, speed_kmh, heading, fuel_pct?, ignition}],   ≤ 50 000 points
//     truncated, next_from,          // when truncated: call again with from = next_from
//     events: [EventV1 subset],      // markers (event_time, lat, lon, type, severity, …)
//     stage_bands: [{stage, from, to, trip_id}],
//     stops: [{from, to, lat, lon, duration_s, ignition, motion, geofence?}],
//     idles: [{from, to, lat, lon, duration_s, geofence?}],
//     visits: [{id, geofence_id, name, purpose_type, entered_at, exited_at, dwell_s, …}],
//     deviations: [{id, trip_id, state, from, to, off_route_km, approved, max_offset_m, …}],
//     planned: {route_id, route_code, route_version, geometry, corridor_m, corridor} | null,
//     planned_legs: [same shape per leg with a pinned route; `replaced` marks the old plan],
//     fuel_series: [{t, level_pct, used_l}], speed_series: [{t, kmh, limit?}],
//     trips: [{id, reference, stage, planned_start, dispatched_at, completed_at}],
//     stats: {points: {read, drawable, returned}, simplifier, ms} }
//
// tolerance_m: Douglas–Peucker tolerance in metres (default 25; 0 = full resolution — use
// it for the zoomed-in window, FR-RPL-013 "full-resolution points load on zoom").
//
// ST-00277: getReplayBatch — GET /replay/batch, up to 25 of the organisation's own trucks over
// one window (≤ 7 days) in one request (types/lexistar-replay ReplayBatchResponse). Converted
// from lexistarReplay.js in the same change (CLAUDE.md TypeScript rule).

import api from './api';
import type { ReplayBatchResponse, ReplayResponse } from '../types/lexistar-replay';

const LX = '/lexistar';

type Instant = string | Date | null | undefined;
type Params = Record<string, unknown>;

const clean = (params: Params = {}): Params =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
const isoOf = (v: Instant): string | null | undefined => (v instanceof Date ? v.toISOString() : v);

export interface ReplayOptions {
  from?: Instant;
  to?: Instant;
  tolerance_m?: number;
  series_points?: number;
}

/** Replay a truck over a range: { asset_key, from, to, tolerance_m?, series_points? } (≤ 31 days). */
export const getAssetReplay = ({
  asset_key,
  from,
  to,
  tolerance_m,
  series_points,
}: ReplayOptions & { asset_key?: string } = {}): Promise<ReplayResponse> =>
  api
    .get(`${LX}/replay`, {
      params: clean({ asset_key, from: isoOf(from), to: isoOf(to), tolerance_m, series_points }),
    })
    .then((res) => res.data as ReplayResponse);

/** Replay a trip (its own window; from / to override it). */
export const getTripReplay = (
  tripId: string,
  { from, to, tolerance_m, series_points }: ReplayOptions = {}
): Promise<ReplayResponse> =>
  api
    .get(`${LX}/replay`, {
      params: clean({
        trip_id: tripId,
        from: isoOf(from),
        to: isoOf(to),
        tolerance_m,
        series_points,
      }),
    })
    .then((res) => res.data as ReplayResponse);

/** Either form: { trip_id } or { asset_key, from, to } plus options. */
export const getReplay = (
  params: ReplayOptions & { trip_id?: string; asset_key?: string } = {}
): Promise<ReplayResponse> =>
  api
    .get(`${LX}/replay`, {
      params: clean({ ...params, from: isoOf(params.from), to: isoOf(params.to) }),
    })
    .then((res) => res.data as ReplayResponse);

/**
 * ST-00277: several trucks over one window — { asset_keys (≤ 25), from, to (≤ 7 days),
 * tolerance_m? }. The keys go as one comma list.
 */
export const getReplayBatch = ({
  asset_keys,
  from,
  to,
  tolerance_m,
}: {
  asset_keys: readonly string[];
  from: Instant;
  to: Instant;
  tolerance_m?: number;
}): Promise<ReplayBatchResponse> =>
  api
    .get(`${LX}/replay/batch`, {
      params: clean({ asset_keys: asset_keys.join(','), from: isoOf(from), to: isoOf(to), tolerance_m }),
    })
    .then((res) => res.data as ReplayBatchResponse);

/**
 * Follow `next_from` until the whole range is loaded (progressive loading of a long
 * track). Calls onPage(page) for each page; resolves to the pages.
 */
export async function getReplayPaged(
  params: ReplayOptions & { trip_id?: string; asset_key?: string },
  { onPage, maxPages = 20 }: { onPage?: (page: ReplayResponse) => void; maxPages?: number } = {}
): Promise<ReplayResponse[]> {
  const pages: ReplayResponse[] = [];
  let from: Instant = params.from;
  for (let i = 0; i < maxPages; i++) {
    const page = await getReplay({ ...params, from });
    pages.push(page);
    if (onPage) onPage(page);
    if (!page.truncated || !page.next_from) break;
    from = page.next_from;
  }
  return pages;
}
